import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { stk } from '../../styles/stocktakeUi';
import { CountTargetsPanel, computeCountProgress } from './countTargets';
import { StocktakeService } from '../../api/services/stocktakeService';
import {
  IAddition,
  ICountLine,
  ICountSubmissionRequest,
} from '../../types/api/request/stocktakerequest';
import {
  IBinLine,
  IBinContentsResponse,
  IPostResultResponse,
  IProductSearchItem,
  IReviewResponse,
  IStocktakeBin,
  IStocktakeLocation,
} from '../../types/api/response/stocktakeresponse';

type Step = 'pick' | 'count' | 'review' | 'result';

// An addition being edited on the count screen. `uid` is a client-only key (stripped before submit).
type DraftAddition = IAddition & { uid: number };

const lineKey = (sku: string, batch: string | null, expiry: string | null): string =>
  `${sku}|${batch ?? ''}|${(expiry ?? '').slice(0, 10)}`;

// Forgiving bin match: ignore case, separators (hyphens/spaces), and leading zeros, so a bin
// "K-07E" is found by typing "K07e", "k7e", "K-07-E", etc. Each digit run is reduced to its
// integer value ("07" -> "7") so the count person never has to remember zero-padding.
const normalizeBin = (s: string): string =>
  (s ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/\d+/g, (m) => String(parseInt(m, 10)));

// Aisle = the bin-name prefix before the first hyphen (e.g. "K-07C" -> "K", "1-02A" -> "1").
// Named bins without a hyphen (Bread Gandola, Cigarette Cabinet) group under "Other".
const binAisle = (name: string): string => {
  const i = (name ?? '').indexOf('-');
  return i > 0 ? name.slice(0, i).trim().toUpperCase() : 'Other';
};
// Aisle chips: natural order (A, B … Z, 1, 2 …) with "Other" pinned last.
const aisleSort = (a: string, b: string): number => {
  if (a === 'Other') return 1;
  if (b === 'Other') return -1;
  return a.localeCompare(b, undefined, { numeric: true });
};

// Show the printed expiry the Fiji way (DD/MM/YYYY) so it matches what's on the product.
const fmtDate = (d: string | null): string => {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-');
  return y && m && day ? `${day}/${m}/${y}` : d;
};

// Subtitle under a line's title: include the SKU only when it isn't already the title (i.e. a real
// product name is shown), plus the expiry. Avoids "SK-123 / SK-123 · exp …" when the name is missing.
const lineSubtitle = (
  sku: string,
  name: string | null | undefined,
  expiry: string | null | undefined,
): string => {
  const parts: string[] = [];
  if (name && name !== sku) parts.push(sku);
  if (expiry) parts.push(`exp ${fmtDate(expiry)}`);
  return parts.join(' · ');
};

// Only FEFO methods capture expiry (First-Expired-First-Out — expiry is printed & meaningful).
// FIFO / FIFO - Batch / serial are counted as a plain total, so their "found" additions take no date.
const isExpiryTracked = (costingMethod: string | null): boolean =>
  !!costingMethod && costingMethod.trim().toUpperCase().startsWith('FEFO');

const errMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message ?? err?.message ?? 'Something went wrong.';
};

// Bins counted several times a day — pinned to the top of the picker. Names must match the Cin7
// bin names exactly. (Simple stand-in until ABC scheduling lands in a later phase.)
const DAILY_BINS = ['Bread Gandola', 'Cigarette Cabinet'];

// Fresh-produce bins — counted daily, pinned in their own section. Names must match Cin7 exactly.
const FRESH_PRODUCE_BINS = ['Z-02'];

// Fresh-meat bins — counted daily too, pinned in their own section. Names must match Cin7 exactly.
const FRESH_MEAT_BINS = ['Z-04', 'I-06A', 'I-06B', 'I-06C', 'I-06D', 'I-06E', 'I-06F'];

// "Last counted" caption on each bin card: green = fresh (≤7 days), grey = older, red = never.
// A tick marks bins already counted today — the "which bins are done" signal for the target.
const lastCountedLabel = (b: IStocktakeBin): string =>
  b.days_since == null ? 'never' : b.days_since === 0 ? '✓ today' : `${b.days_since}d ago`;
const freshTone = (b: IStocktakeBin): string =>
  b.days_since == null ? stk.red : b.days_since <= 7 ? stk.green : '#94a3b8';

// ── Presentational helpers (pure styling — no state, no logic) ────────────────

/** Small overline section header with a coloured dot. */
const SectionLabel = ({ dot, children }: { dot: string; children: ReactNode }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot }} />
    <Typography
      variant="overline"
      sx={{ color: stk.inkSoft, fontWeight: 700, letterSpacing: 1, lineHeight: 1.4 }}
    >
      {children}
    </Typography>
  </Stack>
);

/** Tinted pinned-bin card (rejected = red, daily = amber). */
const PIN_TONES = {
  red: { bg: stk.redBg, line: stk.redLine, main: stk.red },
  amber: { bg: stk.amberBg, line: stk.amberLine, main: stk.amber },
  produce: { bg: stk.greenBg, line: stk.greenLine, main: stk.green },
  meat: { bg: '#fff1f2', line: '#fecdd3', main: '#be123c' },
  blue: { bg: stk.blueBg, line: stk.blueLine, main: stk.blue },
} as const;
const PinCard = ({
  title,
  caption,
  tone,
  onClick,
  disabled,
}: {
  title: string;
  caption?: string;
  tone: keyof typeof PIN_TONES;
  onClick: () => void;
  disabled: boolean;
}) => {
  const t = PIN_TONES[tone];
  return (
    <Card elevation={0} sx={{ bgcolor: t.bg, border: `1px solid ${t.line}`, borderRadius: 2.5 }}>
      <CardActionArea
        onClick={onClick}
        disabled={disabled}
        sx={{ px: 1.75, py: 0.75, textAlign: 'center' }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: stk.ink, fontFamily: stk.mono, letterSpacing: 0.3 }}
        >
          {title}
        </Typography>
        {caption && (
          <Typography
            variant="caption"
            sx={{ color: t.main, fontWeight: 600, display: 'block', lineHeight: 1.3 }}
          >
            {caption}
          </Typography>
        )}
      </CardActionArea>
    </Card>
  );
};

/** Round tap-friendly stepper button (44px — thumb-sized for phones). */
const RoundBtn = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => (
  <Button
    onClick={onClick}
    sx={{
      minWidth: 44,
      width: 44,
      height: 44,
      borderRadius: '50%',
      border: `1px solid ${stk.line}`,
      color: stk.ink,
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 1,
    }}
  >
    {children}
  </Button>
);

/** Action bar pinned to the bottom of the viewport while the list scrolls. */
const StickyBar = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      position: 'sticky',
      bottom: 0,
      zIndex: 5,
      mt: 2,
      py: 1.5,
      bgcolor: '#fff',
      borderTop: `1px solid ${stk.line}`,
    }}
  >
    {children}
  </Box>
);

/** Pick → Count → Review → Done progress pills for the header band. */
const STEP_LABELS: { key: Step; label: string }[] = [
  { key: 'pick', label: 'Pick' },
  { key: 'count', label: 'Count' },
  { key: 'review', label: 'Review' },
  { key: 'result', label: 'Done' },
];
const StepPills = ({ step }: { step: Step }) => {
  const cur = STEP_LABELS.findIndex((s) => s.key === step);
  return (
    <Stack direction="row" spacing={0.75} aria-label="Count progress">
      {STEP_LABELS.map((s, i) => {
        const done = i < cur;
        const active = i === cur;
        return (
          <Chip
            key={s.key}
            label={s.label}
            size="small"
            // A tick on the banked steps, so a counter can see at a glance what's already done.
            icon={done ? <CheckRoundedIcon /> : undefined}
            aria-current={active ? 'step' : undefined}
            sx={(theme) => ({
              fontWeight: active ? 700 : 500,
              ...(done && {
                // Tonal (not solid) success — reads as "banked" without competing with the live step.
                bgcolor: alpha(theme.palette.success.main, 0.14),
                color: 'success.main',
                '& .MuiChip-icon': { color: 'success.main', fontSize: 16 },
              }),
              ...(active && { bgcolor: 'primary.main', color: 'primary.contrastText' }),
              ...(!done && !active && { bgcolor: 'action.hover', color: 'text.secondary' }),
            })}
          />
        );
      })}
    </Stack>
  );
};

export const WarehousePage = () => {
  const [step, setStep] = useState<Step>('pick');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [locations, setLocations] = useState<IStocktakeLocation[]>([]);
  const [activeLot, setActiveLot] = useState<string>('');
  const [binFilter, setBinFilter] = useState('');
  const [activeAisle, setActiveAisle] = useState<string | null>(null);
  const [binSort, setBinSort] = useState<'name' | 'stale'>('name');
  // Which daily-count prefix groups (e.g. "I", "Z") are expanded. Empty = all collapsed (default).
  const [openDailyGroups, setOpenDailyGroups] = useState<Set<string>>(new Set());

  const [session, setSession] = useState<IBinContentsResponse | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [additions, setAdditions] = useState<DraftAddition[]>([]);
  const uidRef = useRef(0);
  const nextUid = () => (uidRef.current += 1);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<IProductSearchItem[]>([]);

  const [review, setReview] = useState<IReviewResponse | null>(null);
  const [zeroConfirmed, setZeroConfirmed] = useState(false);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<IPostResultResponse | null>(null);

  // Count screen is an accordion: one product section open at a time. Finishing one (its "Next"
  // button, or Enter past the last row) auto-advances to the next so the counter never hunts for it.
  const [openSku, setOpenSku] = useState<string | null>(null);
  const qtyInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const firstKeyBySku = useRef<Record<string, string>>({});

  // Open a product section and focus its quantity input so the counter can type straight away.
  useEffect(() => {
    if (!openSku) return;
    const key = firstKeyBySku.current[openSku];
    if (!key) return;
    // preventScroll: focusing the first quantity field must not scroll the allocated-stock warning
    // (rendered above the first product) off the top of the screen the moment a bin opens.
    const t = setTimeout(() => qtyInputRefs.current[key]?.focus({ preventScroll: true }), 60);
    return () => clearTimeout(t);
  }, [openSku]);

  // BARCODE PROVISION — barcode scanning is NOT rolled out yet; this is only the seam it will use.
  // A future scanner, after a successful scan, calls barcodeScanRef.current(scannedSku): that opens
  // the matching product's section and (via the effect above) focuses the quantity field, so the
  // user is prompted to just enter the count. For a SKU not listed in the bin, the scanner should
  // fall through to the existing "found a product" add flow (doSearch / addFoundProduct). Wire a
  // <BarcodeScanner onScan={barcodeScanRef.current} /> onto the count screen when the feature ships.
  const scanToProduct = (scannedSku: string) => setOpenSku(scannedSku);
  const barcodeScanRef = useRef(scanToProduct);
  barcodeScanRef.current = scanToProduct;

  useEffect(() => {
    void loadLocations();
  }, []);

  const loadLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await StocktakeService.getLocations();
      const locs = res.locations ?? [];
      setLocations(locs);
      if (locs.length) setActiveLot((cur) => cur || locs[0].id);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const openBin = async (loc: IStocktakeLocation, bin: IStocktakeBin) => {
    setLoading(true);
    setError(null);
    try {
      const res = await StocktakeService.openBin(loc.name, bin.id);
      setSession(res);
      const init: Record<string, string> = {};
      (res.lines ?? []).forEach((l) => {
        init[lineKey(l.sku, l.batch_sn, l.expiry)] = '';
      });
      setCounts(init);
      setAdditions([]);
      // Open the first product section straight away (accordion + auto-advance from there).
      const firstGroup = groupLines(res.lines ?? [])[0];
      setOpenSku(firstGroup ? firstGroup.sku : null);
      setStep('count');
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // Resolve the pinned daily bins from the loaded locations, in DAILY_BINS order. No extra API call.
  const dailyBins = DAILY_BINS.flatMap((name) =>
    locations.flatMap((loc) =>
      loc.bins.filter((b) => b.name === name).map((bin) => ({ loc, bin })),
    ),
  );

  // Fresh-produce bins, resolved the same way (FRESH_PRODUCE_BINS order).
  const freshProduceBins = FRESH_PRODUCE_BINS.flatMap((name) =>
    locations.flatMap((loc) =>
      loc.bins.filter((b) => b.name === name).map((bin) => ({ loc, bin })),
    ),
  );

  // Fresh-meat bins, resolved the same way (FRESH_MEAT_BINS order).
  const freshMeatBins = FRESH_MEAT_BINS.flatMap((name) =>
    locations.flatMap((loc) =>
      loc.bins.filter((b) => b.name === name).map((bin) => ({ loc, bin })),
    ),
  );

  // Combine the daily-cadence pins (each keeps its category tone), then group any bins that share an
  // aisle-letter code prefix — I-06A…I-06F → "I", Z-02 + Z-04 → "Z" — into a collapsible group
  // (collapsed by default). Named bins with no hyphen (Bread Gandola, Cigarette Cabinet) and any
  // prefix with a single member stay as standalone pins. Pure bin-code grouping — no extra data.
  type DailyItem = { loc: IStocktakeLocation; bin: IStocktakeBin; tone: keyof typeof PIN_TONES };
  const dailyItems: DailyItem[] = [
    ...dailyBins.map((x) => ({ ...x, tone: 'amber' as const })),
    ...freshProduceBins.map((x) => ({ ...x, tone: 'produce' as const })),
    ...freshMeatBins.map((x) => ({ ...x, tone: 'meat' as const })),
  ];
  const dailyPrefix = (name: string): string | null => {
    const i = (name ?? '').indexOf('-');
    return i > 0 ? name.slice(0, i).trim().toUpperCase() : null;
  };
  const byPrefix = new Map<string, DailyItem[]>();
  for (const it of dailyItems) {
    const p = dailyPrefix(it.bin.name);
    if (p) byPrefix.set(p, [...(byPrefix.get(p) ?? []), it]);
  }
  const dailyGroups = Array.from(byPrefix.entries())
    .filter(([, items]) => items.length >= 2)
    .map(([prefix, items]) => ({ prefix, items }))
    .sort((a, b) => a.prefix.localeCompare(b.prefix));
  const groupedIds = new Set(dailyGroups.flatMap((g) => g.items.map((it) => it.bin.id)));
  const dailyStandalone = dailyItems.filter((it) => !groupedIds.has(it.bin.id));
  const toggleDailyGroup = (prefix: string) =>
    setOpenDailyGroups((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });

  // Daily / weekly target progress, derived from the same /locations payload (no extra call).
  const countProgress = useMemo(() => computeCountProgress(locations), [locations]);

  // Monday-start week window — must match the weekly-target maths in countTargets.tsx.
  const daysSinceMonday = (new Date().getDay() + 6) % 7;

  // "Count next" suggestions — bin names only (no captions, per Nikhil 20 Jul). Order:
  // 1. rejected recounts (RED — most urgent, a manager sent them back);
  // 2. shrink-prone bins (risk-based: variance_90d dollars from the app's own posted history,
  //    rested >=3 days — the daily pins cover true daily bins);
  // 3. never-counted (A→Z sweep), then the stalest.
  // Excludes the pinned daily/fresh bins and anything counted today.
  const countNext = useMemo(() => {
    const pinned = new Set([...DAILY_BINS, ...FRESH_PRODUCE_BINS, ...FRESH_MEAT_BINS]);
    const rejected = locations.flatMap((loc) =>
      loc.bins
        .filter((b) => b.needs_recount && !pinned.has(b.name))
        .map((bin) => ({ loc, bin, tone: 'red' as const })),
    );
    const eligible = locations.flatMap((loc) =>
      loc.bins
        .filter((b) => !pinned.has(b.name) && !b.needs_recount && b.days_since !== 0)
        .map((bin) => ({ loc, bin, tone: 'blue' as const })),
    );
    const shrinkProne = eligible
      .filter(
        ({ bin }) => (bin.variance_90d ?? 0) > 0 && (bin.days_since == null || bin.days_since >= 3),
      )
      .sort((x, y) => (y.bin.variance_90d ?? 0) - (x.bin.variance_90d ?? 0))
      .slice(0, 4);
    const inShrink = new Set(shrinkProne.map(({ bin }) => bin.id));
    const rest = eligible
      .filter(({ bin }) => !inShrink.has(bin.id))
      .sort((x, y) => {
        const a = x.bin.days_since;
        const b = y.bin.days_since;
        if (a == null && b == null) return x.bin.name.localeCompare(y.bin.name);
        if (a == null) return -1;
        if (b == null) return 1;
        return b - a;
      });
    return [...rejected, ...shrinkProne, ...rest].slice(0, 8);
  }, [locations]);

  const setCount = (key: string, value: string) => setCounts((c) => ({ ...c, [key]: value }));
  const stepCount = (key: string, delta: number) =>
    setCounts((c) => ({ ...c, [key]: String(Math.max(0, (Number(c[key]) || 0) + delta)) }));

  // ── Additions (found stock): each one is edited inline (date + qty steppers + remove). ──
  const addExpiryFor = (sku: string, name: string, productId: string) =>
    setAdditions((a) => [
      ...a,
      {
        uid: nextUid(),
        product_id: productId,
        sku,
        name,
        expiry: '',
        counted_qty: 1,
        costing_method: 'FEFO - Batch',
      },
    ]);

  const addFoundProduct = (p: IProductSearchItem) => {
    setAdditions((a) => [
      ...a,
      {
        uid: nextUid(),
        product_id: p.product_id,
        sku: p.sku,
        name: p.name,
        expiry: '',
        counted_qty: 1,
        costing_method: p.costing_method,
      },
    ]);
    setSearchResults([]);
    setSearchQuery('');
  };

  const setAdditionExpiry = (uid: number, expiry: string) =>
    setAdditions((a) => a.map((x) => (x.uid === uid ? { ...x, expiry } : x)));
  const setAdditionQty = (uid: number, value: string) =>
    setAdditions((a) =>
      a.map((x) => (x.uid === uid ? { ...x, counted_qty: Math.max(0, Number(value) || 0) } : x)),
    );
  const stepAddition = (uid: number, delta: number) =>
    setAdditions((a) =>
      a.map((x) =>
        x.uid === uid ? { ...x, counted_qty: Math.max(0, (x.counted_qty || 0) + delta) } : x,
      ),
    );
  const removeAddition = (uid: number) => setAdditions((a) => a.filter((x) => x.uid !== uid));

  const buildSubmission = (): ICountSubmissionRequest => {
    const lines: ICountLine[] = (session?.lines ?? [])
      .filter((l) => {
        const raw = counts[lineKey(l.sku, l.batch_sn, l.expiry)];
        return raw !== undefined && raw !== '';
      })
      .map((l) => ({
        sku: l.sku,
        batch_sn: l.batch_sn,
        expiry: l.expiry,
        counted_qty: Number(counts[lineKey(l.sku, l.batch_sn, l.expiry)]),
      }));
    const subAdditions: IAddition[] = additions
      .filter((a) => (a.counted_qty || 0) > 0)
      .map((a) => ({
        product_id: a.product_id,
        sku: a.sku,
        name: a.name,
        expiry: a.expiry ? a.expiry : null,
        counted_qty: a.counted_qty,
        costing_method: a.costing_method,
      }));
    return {
      counts: lines,
      additions: subAdditions,
      note: note.trim() ? note.trim() : undefined,
    };
  };

  // Listed lines the counter never touched — these post NOTHING (Cin7 keeps its number). Surfaced
  // loudly at review so a blank box is never mistaken for a counted zero.
  const untouchedLines = (session?.lines ?? []).filter((l) => {
    const raw = counts[lineKey(l.sku, l.batch_sn, l.expiry)];
    return raw === undefined || raw === '';
  });

  // FEFO products (62% of the catalogue) are listed ONE LINE PER EXPIRY, so the whole physical pile
  // can land on one date while a sibling lot of the same product is zeroed — which posts as a large
  // phantom shrink. The generic zero warning only lists SKUs; this names the product and shows the
  // other date it was counted on, which is what reveals it as a split mistake rather than an empty lot.
  const splitLotLines = (() => {
    const bySku = new Map<string, IBinLine[]>();
    for (const l of session?.lines ?? []) {
      const arr = bySku.get(l.sku);
      if (arr) arr.push(l);
      else bySku.set(l.sku, [l]);
    }
    const out: { sku: string; name: string; counted: IBinLine[]; zeroed: IBinLine[] }[] = [];
    bySku.forEach((ls, sku) => {
      if (ls.length < 2) return; // single lot — there is no split to get wrong
      const qtyOf = (l: IBinLine) => counts[lineKey(l.sku, l.batch_sn, l.expiry)];
      const counted = ls.filter((l) => Number(qtyOf(l)) > 0);
      // Only an EXPLICIT 0 is risky — a blank posts nothing and is covered by the untouched warning.
      const zeroed = ls.filter((l) => {
        const raw = qtyOf(l);
        return raw !== undefined && raw !== '' && Number(raw) === 0;
      });
      if (counted.length > 0 && zeroed.length > 0) {
        out.push({ sku, name: ls[0].name, counted, zeroed });
      }
    });
    return out;
  })();

  const doReview = async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const res = await StocktakeService.review(session.session_id, buildSubmission());
      setReview(res);
      setZeroConfirmed(false);
      setStep('review');
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // Fingerprint of the exact payload for this session. A timed-out retry of the SAME counts sends
  // the same token, so the server echoes the stored outcome instead of double-posting to Cin7;
  // edited counts produce a different token and are properly blocked.
  const submitToken = (payload: ICountSubmissionRequest, sessionId: number): string => {
    const s = `${sessionId}|${JSON.stringify(payload.counts)}|${JSON.stringify(payload.additions)}`;
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return `s${sessionId}-${(h >>> 0).toString(36)}-${s.length}`;
  };

  // Warehouse staff hold the phone at arm's length — a real POST gets a clear buzz + beep + big tick
  // so they KNOW it landed without reading. Dry-runs and parked-for-approval get no celebration.
  const signalPosted = () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate([60, 40, 120]);
      const Ctx =
        (
          window as unknown as {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }
        ).AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 880;
        gain.gain.value = 0.08;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.18);
      }
    } catch {
      /* feedback is best-effort — never let it break the post */
    }
  };

  const doPost = async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const payload = buildSubmission();
      const res = await StocktakeService.post(session.session_id, {
        ...payload,
        submit_token: submitToken(payload, session.session_id),
      });
      setResult(res);
      setStep('result');
      if (res.status === 'POSTED') signalPosted();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const doSearch = async () => {
    if (searchQuery.trim().length < 2) return;
    setLoading(true);
    setError(null);
    try {
      const res = await StocktakeService.searchProducts(searchQuery.trim());
      setSearchResults(res.products ?? []);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('pick');
    setSession(null);
    setCounts({});
    setAdditions([]);
    setReview(null);
    setResult(null);
    setError(null);
    setSearchResults([]);
    setSearchQuery('');
    setNote('');
    setActiveAisle(null);
    setBinFilter('');
    setOpenSku(null);
  };

  const hasBlockers = (review?.blockers?.length ?? 0) > 0;
  const needsZeroConfirm = (review?.zero_counts?.length ?? 0) > 0 && !zeroConfirmed;

  // Group a bin's lines by product (one card per product, a row per batch/expiry under it).
  const groupLines = (lines: IBinLine[]) => {
    const groups: {
      sku: string;
      name: string;
      productId: string;
      isFifo: boolean;
      items: IBinLine[];
    }[] = [];
    const idx: Record<string, number> = {};
    lines.forEach((l) => {
      if (idx[l.sku] == null) {
        idx[l.sku] = groups.length;
        // FIFO until proven otherwise: a product with no batch/expiry on any line has a single
        // stock bucket, so it has no "different expiry" to find — just one count.
        groups.push({ sku: l.sku, name: l.name, productId: l.product_id, isFifo: true, items: [] });
      }
      const g = groups[idx[l.sku]];
      g.items.push(l);
      if (l.batch_sn || l.expiry) g.isFifo = false;
    });
    return groups;
  };

  // The editable controls for one addition: native date picker (when batch-tracked), qty steppers, remove.
  const additionRow = (a: DraftAddition, showDate: boolean) => (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
      {showDate && (
        <TextField
          type="date"
          size="small"
          label="New expiry"
          value={(a.expiry ?? '').slice(0, 10)}
          onChange={(e) => setAdditionExpiry(a.uid, e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 168 }}
        />
      )}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Button
          variant="outlined"
          size="small"
          sx={{ minWidth: 36 }}
          onClick={() => stepAddition(a.uid, -1)}
        >
          &minus;
        </Button>
        <TextField
          type="number"
          size="small"
          value={String(a.counted_qty)}
          onChange={(e) => setAdditionQty(a.uid, e.target.value)}
          sx={{ width: 66 }}
          inputProps={{ min: 0, inputMode: 'decimal', style: { textAlign: 'center' } }}
        />
        <Button
          variant="outlined"
          size="small"
          sx={{ minWidth: 36 }}
          onClick={() => stepAddition(a.uid, 1)}
        >
          +
        </Button>
      </Stack>
      <Button size="small" color="error" onClick={() => removeAddition(a.uid)}>
        Remove
      </Button>
    </Stack>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header: title + step progress. Every post is live — dry-run mode was removed (20 Jul). */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="h5" fontWeight={700}>
            Bin Stocktake
          </Typography>
        </Stack>
        <StepPills step={step} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {step === 'pick' && (
        <Stack spacing={2}>
          {/* Daily/weekly bin-count target — unique bins, posted counts only */}
          {locations.length > 0 && <CountTargetsPanel progress={countProgress} />}
          {/* location pills: tap a lot to show its bins */}
          {locations.length > 1 && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {locations.map((loc) => (
                <Chip
                  key={loc.id}
                  label={`${loc.name.split(' - ')[0]} · ${loc.bins.length} bins`}
                  variant={activeLot === loc.id ? 'filled' : 'outlined'}
                  onClick={() => {
                    setActiveLot(loc.id);
                    setActiveAisle(null);
                    setBinFilter('');
                  }}
                  sx={
                    activeLot === loc.id
                      ? {
                          bgcolor: stk.blueBg,
                          color: stk.blue,
                          fontWeight: 700,
                          border: `1px solid ${stk.blue}`,
                          '&:hover': { bgcolor: stk.blueBg },
                        }
                      : { fontWeight: 500, borderColor: stk.line }
                  }
                />
              ))}
            </Stack>
          )}
          {/* 1. Count next — bin names only. Red = rejected recount, blue = suggested
              (shrink-prone first, then never-counted A→Z, then stalest). */}
          {countNext.length > 0 && (
            <Box>
              <SectionLabel dot={stk.blue}>Count next</SectionLabel>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {countNext.map(({ loc, bin, tone }) => (
                  <PinCard
                    key={bin.id}
                    title={bin.name}
                    tone={tone}
                    onClick={() => {
                      setActiveLot(loc.id);
                      void openBin(loc, bin);
                    }}
                    disabled={loading}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {/* 2. Daily counts — standalone pins first, then collapsible groups for bins that share a
              code prefix (I-06A…F → "I", Z-02 + Z-04 → "Z"). Groups start collapsed; tap to expand. */}
          {dailyStandalone.length + dailyGroups.length > 0 && (
            <Box>
              <SectionLabel dot={stk.amber}>Daily counts</SectionLabel>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="flex-start">
                {dailyStandalone.map(({ loc, bin, tone }) => (
                  <PinCard
                    key={bin.id}
                    title={bin.name}
                    caption={lastCountedLabel(bin)}
                    tone={tone}
                    onClick={() => {
                      setActiveLot(loc.id);
                      void openBin(loc, bin);
                    }}
                    disabled={loading}
                  />
                ))}
                {dailyGroups.map(({ prefix, items }) => {
                  const open = openDailyGroups.has(prefix);
                  return (
                    <Box
                      key={prefix}
                      sx={{
                        border: `1px solid ${stk.line}`,
                        borderRadius: 2.5,
                        bgcolor: stk.field,
                      }}
                    >
                      <CardActionArea
                        onClick={() => toggleDailyGroup(prefix)}
                        sx={{ px: 1.5, py: 0.75, borderRadius: 2.5 }}
                        aria-expanded={open}
                        aria-label={`${prefix} — ${items.length} daily bins`}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontFamily: stk.mono,
                              color: stk.ink,
                              letterSpacing: 0.3,
                            }}
                          >
                            {prefix}
                          </Typography>
                          <Typography variant="caption" sx={{ color: stk.inkSoft }}>
                            · {items.length} bins
                          </Typography>
                          <ExpandMoreRoundedIcon
                            fontSize="small"
                            sx={{
                              color: stk.inkSoft,
                              transition: 'transform .15s',
                              transform: open ? 'none' : 'rotate(-90deg)',
                            }}
                          />
                        </Stack>
                      </CardActionArea>
                      {open && (
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ px: 1, pb: 1, pt: 0.25 }}
                        >
                          {items.map(({ loc, bin, tone }) => (
                            <PinCard
                              key={bin.id}
                              title={bin.name}
                              caption={lastCountedLabel(bin)}
                              tone={tone}
                              onClick={() => {
                                setActiveLot(loc.id);
                                void openBin(loc, bin);
                              }}
                              disabled={loading}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
          <TextField
            fullWidth
            size="small"
            label="Search bin (e.g. K07e)"
            value={binFilter}
            onChange={(e) => {
              setBinFilter(e.target.value);
              if (e.target.value) setActiveAisle(null);
            }}
            autoComplete="off"
          />
          {locations.length === 0 && !loading && (
            <Typography color="text.secondary">No locations available.</Typography>
          )}
          {locations
            .filter((loc) => loc.id === activeLot)
            .map((loc) => {
              const aisles = Array.from(new Set(loc.bins.map((b) => binAisle(b.name)))).sort(
                aisleSort,
              );
              // Per-aisle "counted this week / total bins" tally for the jump bar.
              const aisleTally = new Map<string, { tot: number; wk: number }>();
              loc.bins.forEach((b) => {
                const k = binAisle(b.name);
                const t = aisleTally.get(k) ?? { tot: 0, wk: 0 };
                t.tot += 1;
                if (b.days_since != null && b.days_since <= daysSinceMonday) t.wk += 1;
                aisleTally.set(k, t);
              });
              const q = normalizeBin(binFilter);
              // Search wins; else the picked aisle; else nothing (show the browse hint).
              const matches = q
                ? loc.bins.filter((b) => normalizeBin(b.name).includes(q))
                : activeAisle
                  ? loc.bins.filter((b) => binAisle(b.name) === activeAisle)
                  : [];
              // "Stalest first" puts never-counted at the very top, then the oldest counts.
              const ordered =
                binSort === 'stale'
                  ? [...matches].sort((a, b) => {
                      if (a.days_since == null && b.days_since == null)
                        return a.name.localeCompare(b.name);
                      if (a.days_since == null) return -1;
                      if (b.days_since == null) return 1;
                      return b.days_since - a.days_since;
                    })
                  : matches;
              const shown = ordered.slice(0, 60);
              const browsing = !q && !activeAisle;
              return (
                <Box key={loc.id}>
                  {/* Aisle jump bar — browse straight to a row without typing */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.25,
                      mb: 1.5,
                      bgcolor: stk.slateBg,
                      border: `1px solid ${stk.line}`,
                      borderRadius: 2.5,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: stk.inkSoft,
                        fontWeight: 700,
                        letterSpacing: 1,
                        display: 'block',
                        mb: 0.5,
                        lineHeight: 1.4,
                      }}
                    >
                      Jump to aisle
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {aisles.map((a) => (
                        <Chip
                          key={a}
                          label={a}
                          size="small"
                          variant={activeAisle === a ? 'filled' : 'outlined'}
                          onClick={() => {
                            setBinFilter('');
                            setActiveAisle((cur) => (cur === a ? null : a));
                          }}
                          sx={
                            activeAisle === a
                              ? {
                                  bgcolor: stk.blueBg,
                                  color: stk.blue,
                                  fontWeight: 700,
                                  border: `1px solid ${stk.blue}`,
                                  '&:hover': { bgcolor: stk.blueBg },
                                }
                              : { bgcolor: '#fff', fontWeight: 600, borderColor: stk.line }
                          }
                        />
                      ))}
                    </Stack>
                  </Paper>

                  {browsing ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: stk.inkSoft }}>
                        Search a bin above, or pick an aisle to browse.
                      </Typography>
                    </Box>
                  ) : shown.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No bins match.
                    </Typography>
                  ) : (
                    <>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {q ? 'Results' : `Aisle ${activeAisle}`}{' '}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: stk.inkSoft }}
                          >
                            ({matches.length} bin{matches.length === 1 ? '' : 's'}
                            {!q && activeAisle
                              ? ` · ${aisleTally.get(activeAisle)?.wk ?? 0} counted this week`
                              : ''}
                            )
                          </Typography>
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          {(['name', 'stale'] as const).map((s) => (
                            <Chip
                              key={s}
                              label={s === 'name' ? 'A–Z' : 'Stalest first'}
                              size="small"
                              variant={binSort === s ? 'filled' : 'outlined'}
                              onClick={() => setBinSort(s)}
                              sx={
                                binSort === s
                                  ? { bgcolor: stk.blueBg, color: stk.blue, fontWeight: 700 }
                                  : { fontWeight: 500, borderColor: stk.line }
                              }
                            />
                          ))}
                        </Stack>
                      </Stack>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
                          gap: 1.25,
                        }}
                      >
                        {shown.map((bin) => (
                          <Card
                            key={bin.id}
                            elevation={0}
                            sx={{
                              border: `1px solid ${stk.line}`,
                              borderTop: `3px solid ${freshTone(bin)}`,
                              borderRadius: 2,
                              transition: 'background-color .12s ease',
                              '&:hover': { bgcolor: stk.slateBg },
                            }}
                          >
                            <CardActionArea onClick={() => openBin(loc, bin)} sx={{ p: 1.5 }}>
                              <Typography
                                align="center"
                                sx={{
                                  fontWeight: 700,
                                  fontFamily: stk.mono,
                                  letterSpacing: 0.5,
                                  color: stk.ink,
                                }}
                              >
                                {bin.name}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="center"
                                alignItems="center"
                                sx={{ mt: 0.25 }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: freshTone(bin),
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: stk.inkSoft }}>
                                  {lastCountedLabel(bin)}
                                </Typography>
                              </Stack>
                            </CardActionArea>
                          </Card>
                        ))}
                      </Box>
                      {matches.length > shown.length && (
                        <Typography variant="caption" color="text.secondary">
                          +{matches.length - shown.length} more — type to narrow it down
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              );
            })}
        </Stack>
      )}

      {step === 'count' &&
        session &&
        (() => {
          const lines = session.lines ?? [];
          const allocTotal = lines.reduce((s, l) => s + (l.allocated || 0), 0);
          const groups = groupLines(lines);
          const binSkus = new Set(lines.map((l) => l.sku));
          const newProductAdds = additions.filter((a) => !binSkus.has(a.sku));
          // Auto-advance: open the next product section, or collapse all when past the last one.
          const advance = (sku: string) => {
            const i = groups.findIndex((x) => x.sku === sku);
            const next = i >= 0 ? groups[i + 1] : undefined;
            setOpenSku(next ? next.sku : null);
          };
          const isLastGroup = (sku: string) =>
            groups.length > 0 && groups[groups.length - 1].sku === sku;
          return (
            <Stack spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  px: 2.5,
                  py: 1.75,
                  borderRadius: 2,
                  border: `1px solid ${stk.line}`,
                  borderLeft: `4px solid ${stk.green}`,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: stk.inkSoft, letterSpacing: 1.2, lineHeight: 1.6, display: 'block' }}
                >
                  Counting bin
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, fontFamily: stk.mono, lineHeight: 1.1, color: stk.ink }}
                >
                  {session.bin_code}
                </Typography>
                <Typography variant="caption" sx={{ color: stk.inkSoft }}>
                  {session.location_name}
                </Typography>
              </Paper>

              {allocTotal > 0 ? (
                <Alert severity="warning">
                  ⚠ {allocTotal} unit(s) in this bin are allocated to open orders. SOP is to clear
                  allocations to 0 first. You can still count, but a count below the allocated
                  quantity will be blocked.
                </Alert>
              ) : (
                <Alert severity="success">No allocated stock — clear to count.</Alert>
              )}
              <Typography variant="body2" sx={{ color: stk.inkSoft }}>
                On-hand is hidden — count what is physically there. A blank line is NOT counted (its
                stock stays as-is).
              </Typography>

              {/* one collapsible card per product; entering the count auto-advances to the next */}
              {groups.map((g) => {
                const groupKeys = g.items.map((l) => lineKey(l.sku, l.batch_sn, l.expiry));
                firstKeyBySku.current[g.sku] = groupKeys[0];
                const groupAdds = additions.filter((a) => a.sku === g.sku);
                const entered = groupKeys.filter((k) => (counts[k] ?? '') !== '').length;
                const total = groupKeys.length;
                const allEntered = total > 0 && entered === total;
                const isOpen = openSku === g.sku;
                const statusColor = allEntered ? stk.green : entered > 0 ? stk.amber : stk.inkSoft;
                const statusText = allEntered
                  ? total === 1
                    ? `counted ${counts[groupKeys[0]]}`
                    : `all ${total} counted`
                  : entered > 0
                    ? `${entered}/${total} counted`
                    : 'not counted';
                const toggle = () => setOpenSku((cur) => (cur === g.sku ? null : g.sku));
                return (
                  <Card
                    key={g.sku}
                    elevation={0}
                    sx={{
                      border: `1px solid ${isOpen ? stk.lineStrong : stk.line}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    {/* header — tap to expand / collapse */}
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      aria-label={`${g.name} — ${statusText}`}
                      onClick={toggle}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle();
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        bgcolor: isOpen ? '#fff' : stk.slateBg,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{ fontWeight: 600, color: stk.ink, lineHeight: 1.3 }}
                        >
                          {g.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: stk.inkSoft, fontFamily: stk.mono }}
                        >
                          {g.sku}
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {allEntered && <CheckCircleIcon sx={{ fontSize: 18, color: stk.green }} />}
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: statusColor, whiteSpace: 'nowrap' }}
                        >
                          {statusText}
                          {groupAdds.length > 0 ? ` · +${groupAdds.length} found` : ''}
                        </Typography>
                        <ExpandMoreRoundedIcon
                          sx={{
                            color: stk.inkSoft,
                            transform: isOpen ? 'rotate(180deg)' : 'none',
                            transition: 'transform .15s ease',
                          }}
                        />
                      </Stack>
                    </Box>

                    <Collapse in={isOpen} unmountOnExit>
                      <CardContent sx={{ pt: 0 }}>
                        {g.items.map((l) => {
                          const key = lineKey(l.sku, l.batch_sn, l.expiry);
                          const touched = (counts[key] ?? '') !== '';
                          return (
                            <Stack
                              key={key}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{
                                mt: 1.5,
                                p: 1,
                                borderRadius: 2,
                                bgcolor: touched ? stk.greenBg : stk.field,
                                border: `1px solid ${touched ? stk.greenLine : stk.lineStrong}`,
                                transition: 'background-color .15s ease, border-color .15s ease',
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {l.expiry ? `Exp ${fmtDate(l.expiry)}` : 'No expiry'}
                                </Typography>
                                {l.allocated > 0 && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: stk.red, fontWeight: 600 }}
                                  >
                                    {l.allocated} allocated
                                  </Typography>
                                )}
                              </Box>
                              <Stack direction="row" alignItems="center" spacing={0.75}>
                                <RoundBtn onClick={() => stepCount(key, -1)}>&minus;</RoundBtn>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={counts[key] ?? ''}
                                  onChange={(e) => setCount(key, e.target.value)}
                                  placeholder="–"
                                  inputRef={(el: HTMLInputElement | null) => {
                                    qtyInputRefs.current[key] = el;
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const i = groupKeys.indexOf(key);
                                      if (i < groupKeys.length - 1) {
                                        qtyInputRefs.current[groupKeys[i + 1]]?.focus();
                                      } else {
                                        advance(g.sku);
                                      }
                                    }
                                  }}
                                  sx={{
                                    width: 76,
                                    bgcolor: '#fff',
                                    borderRadius: 1,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: stk.lineStrong,
                                    },
                                  }}
                                  inputProps={{
                                    min: 0,
                                    inputMode: 'decimal',
                                    enterKeyHint: 'next',
                                    style: {
                                      textAlign: 'center',
                                      fontSize: 18,
                                      fontWeight: 600,
                                      padding: '10px 6px',
                                    },
                                  }}
                                />
                                <RoundBtn onClick={() => stepCount(key, 1)}>+</RoundBtn>
                              </Stack>
                            </Stack>
                          );
                        })}

                        {/* expiries the counter found for THIS product — editable inline */}
                        {groupAdds.map((a) => (
                          <Box
                            key={a.uid}
                            sx={{
                              mt: 1.5,
                              pl: 1.5,
                              py: 1,
                              borderLeft: '3px solid',
                              borderColor: 'info.main',
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="info.main"
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              {g.isFifo ? 'Found — extra units' : 'Found — new expiry'}
                            </Typography>
                            {additionRow(a, !g.isFifo)}
                          </Box>
                        ))}

                        {g.isFifo ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1.5, display: 'block' }}
                          >
                            No expiry on this product — just count the total above (include any
                            extras).
                          </Typography>
                        ) : (
                          <Button
                            variant="text"
                            size="small"
                            sx={{ mt: 1.5 }}
                            onClick={() => addExpiryFor(g.sku, g.name, g.productId)}
                          >
                            + Found a different expiry of this product
                          </Button>
                        )}

                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => advance(g.sku)}
                          sx={{
                            mt: 2,
                            py: 1,
                            fontWeight: 700,
                            boxShadow: 'none',
                          }}
                        >
                          {isLastGroup(g.sku) ? 'Done' : 'Next product'}
                        </Button>
                      </CardContent>
                    </Collapse>
                  </Card>
                );
              })}

              <Divider>Found a product not listed in this bin?</Divider>
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search name or SKU"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button variant="outlined" onClick={doSearch}>
                  Search
                </Button>
              </Stack>
              {searchResults.map((p) => (
                <Card key={p.product_id} variant="outlined">
                  <CardActionArea onClick={() => addFoundProduct(p)} sx={{ p: 1.5 }}>
                    <Typography>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.sku} · {p.costing_method ?? 'FIFO'}
                    </Typography>
                  </CardActionArea>
                </Card>
              ))}

              {/* found products that weren't listed in this bin — editable inline */}
              {newProductAdds.map((a) => (
                <Card key={a.uid} variant="outlined" sx={{ borderColor: 'info.main' }}>
                  <CardContent>
                    <Typography>
                      {a.name}{' '}
                      <Typography component="span" variant="caption" color="info.main">
                        (found — new to bin)
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.sku}
                    </Typography>
                    <Box sx={{ mt: 1 }}>{additionRow(a, isExpiryTracked(a.costing_method))}</Box>
                  </CardContent>
                </Card>
              ))}

              <StickyBar>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Button onClick={reset} sx={{ color: stk.inkSoft, minWidth: 0 }}>
                    Cancel
                  </Button>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: stk.inkSoft, display: 'block', textAlign: 'center', mb: 0.5 }}
                    >
                      {lines.length - untouchedLines.length} of {lines.length} counted
                      {untouchedLines.length > 0 ? ` · ${untouchedLines.length} blank` : ''}
                    </Typography>
                    <Box sx={{ height: 4, borderRadius: 2, bgcolor: stk.line }}>
                      <Box
                        sx={{
                          height: '100%',
                          borderRadius: 2,
                          bgcolor: stk.green,
                          width: `${
                            lines.length === 0
                              ? 0
                              : Math.round(
                                  ((lines.length - untouchedLines.length) / lines.length) * 100,
                                )
                          }%`,
                          transition: 'width .2s ease',
                        }}
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={doReview}
                    sx={{ px: 3, py: 1.1, fontWeight: 700 }}
                  >
                    Review
                  </Button>
                </Stack>
              </StickyBar>
            </Stack>
          );
        })()}

      {step === 'review' && review && (
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              px: 2.5,
              py: 1.75,
              borderRadius: 2,
              border: `1px solid ${stk.line}`,
              borderLeft: `4px solid ${stk.green}`,
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: stk.inkSoft, letterSpacing: 1.2, lineHeight: 1.6, display: 'block' }}
            >
              Review count
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, fontFamily: stk.mono, lineHeight: 1.1, color: stk.ink }}
            >
              {review.bin_code}
            </Typography>
          </Paper>

          {/* quick stats */}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {[
              {
                n: review.counted.length,
                label: 'counted',
                bg: stk.greenBg,
                line: stk.greenLine,
                main: stk.green,
              },
              {
                n: review.additions.length,
                label: 'found',
                bg: stk.blueBg,
                line: stk.blueLine,
                main: stk.blue,
              },
              {
                n: untouchedLines.length,
                label: 'blank',
                bg: stk.slateBg,
                line: stk.line,
                main: stk.inkSoft,
              },
            ]
              .filter((s) => s.n > 0)
              .map((s) => (
                <Box
                  key={s.label}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 99,
                    bgcolor: s.bg,
                    border: `1px solid ${s.line}`,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: s.main }}>
                    {s.n} {s.label}
                  </Typography>
                </Box>
              ))}
          </Stack>

          <Paper elevation={0} sx={{ border: `1px solid ${stk.line}`, borderRadius: 2.5 }}>
            {review.counted.map((c, i) => (
              <Box
                key={`${c.sku}-${i}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1.25,
                  borderBottom:
                    i < review.counted.length - 1 || review.additions.length > 0
                      ? `1px solid ${stk.line}`
                      : 'none',
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {c.name ?? c.sku}
                  </Typography>
                  {lineSubtitle(c.sku, c.name, c.expiry) && (
                    <Typography variant="caption" sx={{ color: stk.inkSoft, fontFamily: stk.mono }}>
                      {lineSubtitle(c.sku, c.name, c.expiry)}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{c.counted}</Typography>
              </Box>
            ))}
            {review.additions.map((a, i) => (
              <Box
                key={`add-${a.sku}-${i}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1.25,
                  bgcolor: stk.blueBg,
                  borderBottom: i < review.additions.length - 1 ? `1px solid ${stk.line}` : 'none',
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {a.name ?? a.sku}{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: stk.blue, fontWeight: 700 }}
                    >
                      found
                    </Typography>
                  </Typography>
                  {lineSubtitle(a.sku, a.name, a.expiry) && (
                    <Typography variant="caption" sx={{ color: stk.inkSoft, fontFamily: stk.mono }}>
                      {lineSubtitle(a.sku, a.name, a.expiry)}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{a.counted_qty}</Typography>
              </Box>
            ))}
          </Paper>

          {hasBlockers && (
            <Alert severity="error">
              Count is below the allocated (reserved) quantity for:{' '}
              {review.blockers
                .map((b) => `${b.name ?? b.sku} (${b.counted} < ${b.allocated})`)
                .join('; ')}
              . Clear those orders or recount.
            </Alert>
          )}
          {untouchedLines.length > 0 && (
            <Alert severity="info">
              <strong>
                {untouchedLines.length} listed line{untouchedLines.length > 1 ? 's' : ''} left blank
              </strong>{' '}
              — their stock will <strong>NOT</strong> be changed (Cin7 keeps its current number):{' '}
              {untouchedLines
                .slice(0, 8)
                .map((l) => l.sku)
                .join(', ')}
              {untouchedLines.length > 8 ? `, +${untouchedLines.length - 8} more` : ''}. If any are
              genuinely empty, go back and enter 0.
            </Alert>
          )}
          {review.zero_counts.length > 0 && (
            <Alert severity="warning">
              You counted 0 for stock the system shows on hand:{' '}
              {review.zero_counts.map((z) => z.name ?? z.sku).join(', ')}.
            </Alert>
          )}
          {splitLotLines.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Same product counted on one expiry but zeroed on another — is that right?
              </Typography>
              {splitLotLines.map((s) => (
                <Typography variant="body2" key={s.sku} sx={{ mt: 0.25 }}>
                  <b>{s.name || s.sku}</b> —{' '}
                  {s.counted
                    .map(
                      (l) =>
                        `${counts[lineKey(l.sku, l.batch_sn, l.expiry)]} on ${fmtDate(l.expiry)}`,
                    )
                    .join(', ')}
                  , but <b>0</b> on {s.zeroed.map((l) => fmtDate(l.expiry)).join(', ')}
                </Typography>
              ))}
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
                This product is expiry-tracked, so each date is counted separately. If the whole
                pile went onto one date by mistake, go back and split it — a wrong 0 posts as a
                shrink.
              </Typography>
            </Alert>
          )}

          <TextField
            label="Note for the approver (optional)"
            placeholder="e.g. shelf was messy, carton unopened, damaged units set aside"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
          {review.zero_counts.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={zeroConfirmed}
                  onChange={(e) => setZeroConfirmed(e.target.checked)}
                />
              }
              label="I confirm those zero counts are correct"
            />
          )}

          <StickyBar>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Button onClick={() => setStep('count')} sx={{ color: stk.inkSoft, minWidth: 0 }}>
                Back
              </Button>
              <Button
                variant="contained"
                fullWidth
                color="success"
                disabled={hasBlockers || needsZeroConfirm}
                onClick={doPost}
                sx={{ py: 1.2, fontWeight: 700 }}
              >
                Post to Cin7
              </Button>
            </Stack>
          </StickyBar>
        </Stack>
      )}

      {step === 'result' && result && (
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${stk.line}`,
              borderRadius: 2,
              textAlign: 'center',
              px: 2.5,
              py: 3,
            }}
          >
            {result.status === 'POSTED' && (
              <>
                <CheckCircleIcon color="success" sx={{ fontSize: 96 }} />
                <Typography variant="h6" sx={{ color: stk.green, fontWeight: 800, mb: 1 }}>
                  Posted to Cin7
                </Typography>
              </>
            )}
            <Typography variant="body2" sx={{ color: stk.inkSoft, mb: 1.5 }}>
              {result.message}
            </Typography>
            {result.cin7_stocktake_number && (
              <Box
                sx={{
                  display: 'inline-block',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 99,
                  bgcolor: stk.slateBg,
                  border: `1px solid ${stk.line}`,
                  mb: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontFamily: stk.mono, fontWeight: 700 }}>
                  {result.cin7_stocktake_number}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ color: stk.inkSoft, display: 'block' }}>
              Status {result.status} · mode {result.mode}
            </Typography>
          </Paper>
          {result.adjustments.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Added stock</Typography>
              {result.adjustments.map((a, i) => (
                <Typography key={`${a.sku}-${i}`} variant="body2">
                  {a.sku} × {a.qty}
                  {a.expiry ? ` · exp ${fmtDate(a.expiry)}` : ''}
                  {a.number ? ` (${a.number})` : ''}
                </Typography>
              ))}
            </Box>
          )}
          <Button variant="contained" onClick={reset} sx={{ py: 1.2, fontWeight: 700 }}>
            New count
          </Button>
        </Stack>
      )}
    </Box>
  );
};
